import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckSquare, Square, CalendarBlank, Plus, Paperclip, Trash, DownloadSimple,
  UserPlus, X, FileZip, User
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tender tracking panel: checklist + documents + contacts + ZIP export.
 * Mounted inside the opportunity detail modal/page when type='appel_offres'.
 */
export const TenderTrackingPanel = ({ opportunityId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contactModal, setContactModal] = useState({ open: false });
  const [uploadingFor, setUploadingFor] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/tenders/${opportunityId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement suivi');
    } finally { setLoading(false); }
  }, [opportunityId]);

  useEffect(() => { if (opportunityId) load(); }, [opportunityId, load]);

  const toggleStep = async (stepKey, checked) => {
    try {
      await axios.put(`${API_URL}/api/tenders/${opportunityId}/steps/${stepKey}`, { checked }, { withCredentials: true });
      load();
    } catch { toast.error('Erreur'); }
  };

  const setDueDate = async (stepKey, due_date) => {
    try {
      await axios.put(`${API_URL}/api/tenders/${opportunityId}/steps/${stepKey}`, { due_date }, { withCredentials: true });
      load();
    } catch { toast.error('Erreur'); }
  };

  const uploadFile = async (stepKey, file) => {
    if (!file) return;
    setUploadingFor(stepKey);
    try {
      const fd = new FormData();
      fd.append('step_key', stepKey);
      fd.append('file', file);
      await axios.post(`${API_URL}/api/tenders/${opportunityId}/documents`, fd, { withCredentials: true });
      toast.success(`${file.name} ajoute`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur upload');
    } finally { setUploadingFor(''); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API_URL}/api/tenders/${opportunityId}/documents/${docId}`, { withCredentials: true });
      load();
    } catch { toast.error('Erreur'); }
  };

  const downloadZip = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/tenders/${opportunityId}/compile-zip`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AO_${opportunityId}_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('ZIP genere');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aucun document a compiler');
    }
  };

  const submitContact = async (form) => {
    try {
      await axios.post(`${API_URL}/api/tenders/${opportunityId}/contacts`, form, { withCredentials: true });
      toast.success('Contact ajoute (et copie dans le repertoire client)');
      setContactModal({ open: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API_URL}/api/tenders/${opportunityId}/contacts/${contactId}`, { withCredentials: true });
      load();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div style={{ padding: 20, color: '#5E5F66' }}>Chargement...</div>;
  if (!data) return null;

  const totalDocs = data.steps.reduce((s, st) => s + (st.documents || []).length, 0);

  return (
    <div data-testid="tender-tracking-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Suivi de l'appel d'offres</h3>
        <button className="btn btn-primary btn-sm" onClick={downloadZip} disabled={totalDocs === 0} data-testid="compile-zip-btn">
          <FileZip size={14} weight="bold" /> Compiler le dossier ({totalDocs} doc{totalDocs > 1 ? 's' : ''})
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.steps.map(step => (
          <StepRow
            key={step.key}
            step={step}
            onToggle={(c) => toggleStep(step.key, c)}
            onDueDateChange={(d) => setDueDate(step.key, d)}
            onUpload={(f) => uploadFile(step.key, f)}
            onDeleteDoc={deleteDoc}
            uploading={uploadingFor === step.key}
            isContacts={step.is_contacts}
            contacts={data.contacts || []}
            onAddContact={() => setContactModal({ open: true })}
            onDeleteContact={deleteContact}
          />
        ))}
      </div>

      {contactModal.open && (
        <ContactFormModal onClose={() => setContactModal({ open: false })} onSubmit={submitContact} />
      )}
    </div>
  );
};

const StepRow = ({ step, onToggle, onDueDateChange, onUpload, onDeleteDoc, uploading, isContacts, contacts, onAddContact, onDeleteContact }) => {
  const fileRef = React.useRef(null);
  const docs = step.documents || [];
  return (
    <div className="card" style={{ padding: '12px 14px' }} data-testid={`tender-step-${step.key}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={() => onToggle(!step.checked)} title={step.checked ? 'Decocher' : 'Cocher'}
          data-testid={`step-toggle-${step.key}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: step.checked ? '#16A34A' : '#9CA3AF' }}>
          {step.checked ? <CheckSquare size={22} weight="fill" /> : <Square size={22} />}
        </button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 600, color: step.checked ? '#16A34A' : '#1E2030', textDecoration: step.checked ? 'line-through' : 'none' }}>
            {step.label}
          </div>
        </div>
        {step.ask_date && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CalendarBlank size={14} color="#5E5F66" />
            <input type="date" className="form-input" value={step.due_date || ''}
              onChange={(e) => onDueDateChange(e.target.value)}
              data-testid={`step-date-${step.key}`}
              style={{ padding: '4px 8px', fontSize: '0.85rem', width: 150 }} />
          </div>
        )}
        {!isContacts && (
          <>
            <input type="file" ref={fileRef} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
            <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid={`step-upload-${step.key}`}>
              <Paperclip size={12} /> {uploading ? 'Envoi...' : 'Joindre'}
            </button>
          </>
        )}
        {isContacts && (
          <button className="btn btn-secondary btn-sm" onClick={onAddContact} data-testid="add-tender-contact">
            <UserPlus size={12} /> Ajouter un contact
          </button>
        )}
      </div>

      {docs.length > 0 && (
        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
          {docs.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 6, fontSize: '0.78rem' }}>
              <Paperclip size={12} color="#0369A1" />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.filename}>{d.filename}</span>
              <a href={`${API_URL}${d.download_url}`} target="_blank" rel="noopener noreferrer" title="Telecharger">
                <DownloadSimple size={12} />
              </a>
              <button onClick={() => onDeleteDoc(d.id)} title="Supprimer" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', padding: 0 }}>
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {isContacts && contacts.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {contacts.map(c => (
            <div key={String(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', backgroundColor: '#F4F5F7', borderRadius: 6, fontSize: '0.82rem' }}>
              <User size={14} color="#5E5F66" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{c.first_name} {c.last_name} {c.role && <span style={{ color: '#9CA3AF', fontWeight: 400 }}>· {c.role}</span>}</div>
                <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>
                  {c.organization && <span>{c.organization}</span>}
                  {c.phone && <span> · {c.phone}</span>}
                  {c.email && <span> · {c.email}</span>}
                </div>
              </div>
              <button onClick={() => onDeleteContact(c.id)} title="Supprimer" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828' }}>
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactFormModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '', role: '', organization: '', address: '' });
  const submit = (e) => { e.preventDefault(); onSubmit(form); };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: 'white', borderRadius: 10, width: '95%', maxWidth: 520, padding: 22, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={18} weight="bold" /> Nouveau contact AO
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Prenom</label>
              <input className="form-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="tc-first" />
            </div>
            <div className="form-group">
              <label className="form-label">Nom</label>
              <input className="form-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="tc-last" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Telephone</label>
              <input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tc-phone" />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tc-email" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Fonction</label>
            <input className="form-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="ex: Charge d'affaires" data-testid="tc-role" />
          </div>
          <div className="form-group">
            <label className="form-label">Organisme</label>
            <input className="form-input" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="ex: Mairie de Roubaix" data-testid="tc-org" />
          </div>
          <div className="form-group">
            <label className="form-label">Adresse postale</label>
            <input className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="tc-address" />
          </div>
          <div style={{ fontSize: '0.78rem', color: '#0369A1', backgroundColor: '#F0F9FF', padding: '8px 10px', borderRadius: 6, marginBottom: 12 }}>
            Ce contact sera automatiquement ajoute a votre <strong>repertoire clients</strong>.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" data-testid="tc-save">
              <Plus size={14} /> Ajouter
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
